import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom"
import { usePageTitle } from "../../hooks/usePageTitle.ts"
import { SmartOLTDashboardPage } from "./pages/SmartOLTDashboardPage.tsx"
import { SmartOLTUnconfiguredPage } from "./pages/SmartOLTUnconfiguredPage.tsx"
import { SmartOLTConfiguredPage } from "./pages/SmartOLTConfiguredPage.tsx"
import { SmartOLTOnuDetailPage } from "./pages/SmartOLTOnuDetailPage.tsx"
import { SmartOLTOltDetailPage } from "./pages/SmartOLTOltDetailPage.tsx"
import { SmartOLTTerminatedPage } from "./pages/SmartOLTTerminatedPage.tsx"
import { SmartOLTBillingSuspensionsPage } from "./pages/SmartOLTBillingSuspensionsPage.tsx"
import { SmartOLTAlarmsPage } from "./pages/SmartOLTAlarmsPage.tsx"
import { SmartOLTCapacityPage } from "./pages/SmartOLTCapacityPage.tsx"
import { SmartOLTMapsPage } from "./pages/SmartOLTMapsPage.tsx"
import { SmartOLTLiveTopologyPage } from "./pages/SmartOLTLiveTopologyPage.tsx"
import { SmartOLTXrtgPage } from "./pages/SmartOLTXrtgPage.tsx"
import { SmartOLTDiagnosticsPage } from "./pages/SmartOLTDiagnosticsPage.tsx"
import { SmartOLTReportsPage } from "./pages/SmartOLTReportsPage.tsx"
import { SmartOLTZonesPage } from "./pages/SmartOLTZonesPage.tsx"
import { SmartOLTOdbsPage } from "./pages/SmartOLTOdbsPage.tsx"
import { SmartOLTOnuTypesPage } from "./pages/SmartOLTOnuTypesPage.tsx"
import { SmartOLTSpeedProfilesPage } from "./pages/SmartOLTSpeedProfilesPage.tsx"
import { SmartOLTOltSettingsPage } from "./pages/SmartOLTOltSettingsPage.tsx"
import { SmartOLTOltsPage } from "./pages/SmartOLTOltsPage.tsx"
import { SmartOLTSystemConfigPage } from "./pages/SmartOLTSystemConfigPage.tsx"
import { useSmartOLTAvailability } from "./hooks/useSmartOLTAvailability.ts"

function resolveSmartOLTPageTitle(pathname: string): string {
  if (/\/onus\/unconfigured/.test(pathname)) return "SmartOLT · Unconfigured ONUs"
  if (/\/onus\/terminated/.test(pathname)) return "SmartOLT · Terminated ONUs"
  if (/\/onus\/billing-suspensions/.test(pathname)) return "SmartOLT · Billing Suspensions"
  if (/\/onus\/[^/]+/.test(pathname)) return "SmartOLT · ONU Details"
  if (/\/onus$/.test(pathname)) return "SmartOLT · Configured ONUs"
  if (/\/olts\/[^/]+/.test(pathname)) return "SmartOLT · OLT Details"
  if (/\/olts$/.test(pathname)) return "SmartOLT · OLTs"
  if (pathname.includes("/alarms")) return "SmartOLT · Alarms"
  if (pathname.includes("/capacity")) return "SmartOLT · Capacity"
  if (pathname.includes("/maps")) return "SmartOLT · Maps"
  if (pathname.includes("/topology")) return "SmartOLT · Live Topology"
  if (pathname.includes("/xrtg")) return "SmartOLT · XRTG Graphs"
  if (pathname.includes("/diagnostics")) return "SmartOLT · Diagnostics"
  if (pathname.includes("/reports")) return "SmartOLT · Reports"
  if (pathname.includes("/settings/zones")) return "SmartOLT · Zones"
  if (pathname.includes("/settings/odbs")) return "SmartOLT · ODB / Closures"
  if (pathname.includes("/settings/onu-types")) return "SmartOLT · ONU Types"
  if (pathname.includes("/settings/speed-profiles")) return "SmartOLT · Speed Profiles"
  if (pathname.includes("/settings/olts")) return "SmartOLT · OLT Settings"
  if (pathname.includes("/settings/system")) return "SmartOLT · System Config"
  return "SmartOLT · Dashboard"
}

function SmartOLTLayout() {
  const { pathname } = useLocation()
  usePageTitle(resolveSmartOLTPageTitle(pathname))

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-[#F4F7FA] p-4 sm:p-6 lg:p-8 pb-16 sm:pb-24">
      <div className="mx-auto max-w-[1600px] w-full">
        <Outlet />
      </div>
    </div>
  )
}

export default function SmartOLTRoutes() {
  const { loading, available, tenant } = useSmartOLTAvailability()

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-[#F4F7FA] p-6">
        <div className="mx-auto max-w-[1600px] rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Checking SmartOLT access...
        </div>
      </div>
    )
  }

  if (!available) {
    return <Navigate to="/customer-management" replace />
  }

  if (!tenant?.enabled) {
    return (
      <Routes>
        <Route element={<SmartOLTLayout />}>
          <Route index element={<Navigate to="settings/system" replace />} />
          <Route path="settings/system" element={<SmartOLTSystemConfigPage />} />
          <Route path="*" element={<Navigate to="settings/system" replace />} />
        </Route>
      </Routes>
    )
  }

  return (
    <Routes>
      <Route element={<SmartOLTLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<SmartOLTDashboardPage />} />
        <Route path="onus/unconfigured" element={<SmartOLTUnconfiguredPage />} />
        <Route path="onus/terminated" element={<SmartOLTTerminatedPage />} />
        <Route path="onus/billing-suspensions" element={<SmartOLTBillingSuspensionsPage />} />
        <Route path="billing-suspensions" element={<Navigate to="onus/billing-suspensions" replace />} />
        <Route path="onus/:onuId" element={<SmartOLTOnuDetailPage />} />
        <Route path="onus" element={<SmartOLTConfiguredPage />} />
        <Route path="olts/:oltId" element={<SmartOLTOltDetailPage />} />
        <Route path="olts" element={<SmartOLTOltsPage />} />
        <Route path="alarms" element={<SmartOLTAlarmsPage />} />
        <Route path="capacity" element={<SmartOLTCapacityPage />} />
        <Route path="maps" element={<SmartOLTMapsPage />} />
        <Route path="topology" element={<SmartOLTLiveTopologyPage />} />
        <Route path="xrtg" element={<SmartOLTXrtgPage />} />
        <Route path="diagnostics" element={<SmartOLTDiagnosticsPage />} />
        <Route path="reports" element={<SmartOLTReportsPage />} />
        <Route path="settings/zones" element={<SmartOLTZonesPage />} />
        <Route path="settings/odbs" element={<SmartOLTOdbsPage />} />
        <Route path="settings/onu-types" element={<SmartOLTOnuTypesPage />} />
        <Route path="settings/speed-profiles" element={<SmartOLTSpeedProfilesPage />} />
        <Route path="settings/olts" element={<SmartOLTOltSettingsPage />} />
        <Route path="settings/system" element={<SmartOLTSystemConfigPage />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>
    </Routes>
  )
}
